import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileSignature, ChevronRight } from "lucide-react";
import { PageHeader, PageShell } from "@/components/PageHeader";
import { SectionHeader } from "@/components/SectionHeader";
import { RoleGate } from "@/components/RoleGate";
import { StatusPill } from "@/components/Chips";
import { tourDisplayLabel } from "@/lib/tourDisplayLabel";
import { api } from "@/lib/api";

const fmtRange = (start, end) => {
  const f = (s) => s ? new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";
  return `${f(start)} → ${f(end)}`;
};

/**
 * Quotes & Contracts commercial workspace. Reads authoritative
 * tour + commercial state from `GET /api/tours`. Per-tour Quote and
 * Contract details live on the Tour Detail page's tabs where the
 * write-side flows (Draft/Sent/Executed) already exist. This surface
 * indexes the tours so users can find commercial artifacts quickly.
 *
 * No new backend endpoint required — reuses existing tour data.
 * Quote/contract versions themselves are immutable and edited via
 * the per-tour tabs.
 */

const usd = (n) => {
  if (n == null || n === "") return "—";
  const num = typeof n === "string" ? Number(n) : n;
  if (Number.isNaN(num)) return "—";
  return num.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
};

export default function QuotesContractsPage() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/tours?limit=500")
      .then((r) => setRows(Array.isArray(r.data) ? r.data : (r.data?.results || [])))
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load commercial data"));
  }, []);

  const filtered = (rows || []).filter((t) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return [t.name, t.title, t.customer?.display_name, t.customer?.name, t.customer_name]
      .some((v) => (v || "").toLowerCase().includes(s));
  });

  return (
    <RoleGate permission="quotes.view">
      <PageShell>
        {/* RC4 · shared SectionHeader adoption landmark (sr-only until full visual migration lands progressively). */}
        <SectionHeader testid="page-section-landmark" className="sr-only">Quotes & Contracts</SectionHeader>
        <PageHeader
          testid="page-quotes-contracts"
          title={<span className="inline-flex items-center gap-2"><FileSignature size={22} className="text-coach-blue" />Quotes &amp; Contracts</span>}
          subtitle="All quotes and contracts by tour. Open a tour to view its versioned quotes and executed contract. Quote versions are immutable once sent; contracts are immutable once executed."
        />

        <div className="mb-3 flex items-center justify-between gap-2">
          <input
            type="search"
            placeholder="Search by tour or customer…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-72 max-w-full text-[13px] px-3 py-1.5 border border-coach-silver rounded-sm focus:outline-none focus:border-coach-blue"
            data-testid="quotes-search"
          />
          <div className="text-[11px] uppercase tracking-widest text-steel">
            {rows == null ? "Loading…" : `${filtered.length.toLocaleString()} of ${rows.length.toLocaleString()}`}
          </div>
        </div>

        {err && (
          <div className="bg-amber-hold/10 border border-amber-hold/30 rounded-sm px-4 py-2 text-[13px] text-carbon mb-4" data-testid="quotes-error">
            {err}
          </div>
        )}

        <div className="bg-white border border-coach-silver rounded-md overflow-hidden">
          <table className="w-full text-[13px]" data-testid="quotes-table">
            <thead className="bg-road-white text-[10.5px] uppercase tracking-widest text-steel">
              <tr>
                <th className="text-left px-4 py-2">Tour</th>
                <th className="text-left px-4 py-2 w-40">Customer</th>
                <th className="text-left px-4 py-2 w-48">Dates</th>
                <th className="text-left px-4 py-2 w-32">Quote Status</th>
                <th className="text-left px-4 py-2 w-32">Contract Status</th>
                <th className="text-left px-4 py-2 w-32">Total Value</th>
                <th className="px-2 py-2 w-10" />
              </tr>
            </thead>
            <tbody>
              {rows === null && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-steel">Loading…</td></tr>
              )}
              {rows && filtered.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-steel">No matching tours.</td></tr>
              )}
              {filtered.map((t) => {
                const total = t.contract_value ?? t.estimated_value ?? t.total_value ?? t.grand_total ?? null;
                const clientName = t.customer?.display_name || t.customer?.name || t.customer_name || null;
                const label = tourDisplayLabel(t, { clientName });
                return (
                  <tr key={t.id} data-testid={`quote-row-${t.id}`} className="border-t border-coach-silver/60 hover:bg-road-white">
                    <td className="px-4 py-2">
                      <Link to={`/tours/${t.id}`} className="text-coach-blue hover:underline" data-testid={`quote-tour-${t.id}`}>
                        {label}
                      </Link>
                    </td>
                    <td className="px-4 py-2 text-steel">
                      {clientName || "—"}
                    </td>
                    <td className="px-4 py-2 text-steel">
                      {fmtRange(t.start_date, t.end_date)}
                    </td>
                    <td className="px-4 py-2"><StatusPill status={t.quote_status || t.status || "—"} /></td>
                    <td className="px-4 py-2"><StatusPill status={t.contract_status || "—"} /></td>
                    <td className="px-4 py-2 text-carbon tabular-nums">{usd(total)}</td>
                    <td className="px-2 py-2 text-right text-steel"><ChevronRight size={14} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </PageShell>
    </RoleGate>
  );
}
